import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Button, Chip, CircularProgress, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Divider,
  FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import { 
  Group as GroupIcon, Email as EmailIcon, SupervisorAccount as SupervisorIcon
} from '@mui/icons-material';
import { groupApplicationService } from '../../services/groupApplicationService';
import { groupService } from '../../services/groupService';
import { userService } from '../../services/userService';
import { groupTypeService } from '../../services/groupTypeService';
import { roleService } from '../../services/roleService';
import { getCurrentUser } from '../../utils/auth';

const TeamInviteSupervisors = () => {
  const [myGroups, setMyGroups] = useState([]);
  const [supervisors, setSupervisors] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [groupTypes, setGroupTypes] = useState([]);
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Invitation dialog states
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedSupervisor, setSelectedSupervisor] = useState('');
  const [inviteReason, setInviteReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const currentUser = getCurrentUser();

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      // Load groups where current user is the primary user (team supervisor)
      const allGroups = await groupService.getAllGroups();
      const groupTypesData = await groupTypeService.getAllGroupTypes();
      
      const myGroupsData = allGroups.filter(group => {
        // Check if user is the primary user and group is active
        if (group.primaryUserId !== currentUser?.id || !group.isActive) {
          return false;
        }
        
        // Check if group type matches FTC team pattern
        const groupType = groupTypesData.find(gt => gt.id === group.groupTypeId);
        return groupType && groupType.name && groupType.name.match(/^FTC-.*-Team$/);
      });
      setMyGroups(myGroupsData);
      
      // Load all users and filter team supervisors
      const allUsersData = await userService.getAllUsers();
      const allRoles = await roleService.getAllRoles();
      const supervisorRole = allRoles.find(role => role.name === 'Team Supervisor');
      
      const supervisorsData = allUsersData.filter(user => 
        user.roleIds && supervisorRole && user.roleIds.includes(supervisorRole.id)
      );
      setSupervisors(supervisorsData);
      setAllUsers(allUsersData);
      
      // Set group types (already loaded above)
      setGroupTypes(groupTypesData);
      
      // Load pending invitations for my groups
      const allApplications = await groupApplicationService.getAllApplications();
      const pendingData = allApplications.filter(app => 
        app.applicationType === 'SUPERVISOR_INVITATION' && 
        app.status !== 'APPROVED' && 
        app.status !== 'REJECTED' &&
        myGroupsData.some(group => group.id === app.groupId)
      );
      setPendingInvitations(pendingData);
      
    } catch (err) {
      setError('Failed to load data: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [currentUser?.id]);

  useEffect(() => {
    if (currentUser?.id) {
      loadData();
    }
  }, [currentUser?.id, loadData]);

  const handleInviteSupervisor = (group) => {
    setSelectedGroup(group);
    setSelectedSupervisor('');
    setInviteReason('');
    setInviteDialogOpen(true);
  };

  const handleSubmitInvitation = async () => {
    if (!selectedSupervisor || !inviteReason.trim()) {
      setError('Please select a supervisor and provide a reason for the invitation');
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      // Find the supervisor's household
      const supervisor = supervisors.find(s => s.id === selectedSupervisor);
      if (!supervisor || !supervisor.householdIds || supervisor.householdIds.length === 0) {
        setError('Selected supervisor does not have a valid household');
        return;
      }
      
      // Use the first household ID (supervisors typically belong to one household)
      const householdId = Array.from(supervisor.householdIds)[0];

      await groupApplicationService.submitSupervisorInvitation(
        selectedSupervisor,
        selectedGroup.id,
        householdId,
        inviteReason,
        currentUser.id
      );

      setInviteDialogOpen(false);
      setSuccess(`Invitation sent to ${getSupervisorDisplayName(supervisor)} for team ${selectedGroup.name}`);
      
      // Reload data to update pending invitations
      loadData();
      
      // Hide success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to send invitation: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const getGroupTypeName = (groupTypeId) => {
    const groupType = groupTypes.find(gt => gt.id === groupTypeId);
    return groupType ? groupType.name : 'Unknown Type';
  };

  const getSupervisorDisplayName = (supervisor) => {
    if (!supervisor) return 'Unknown User';
    
    // Try to construct a display name from available fields
    const preferredName = supervisor.preferredName || supervisor.firstName;
    const lastName = supervisor.lastName || '';
    const graduationYear = supervisor.graduationYear || '';
    
    if (preferredName && lastName) {
      return `${preferredName} ${lastName} ${graduationYear}`.trim();
    } else if (supervisor.email) {
      return supervisor.email;
    } else {
      return `User ${supervisor.id}`;
    }
  };

  const isSupervisorAlreadyInGroup = (supervisor, group) => {
    return group && group.userIds && group.userIds.includes(supervisor.id);
  };

  const hasPendingInvitation = (supervisor, group) => {
    return group && pendingInvitations.some(invitation => 
      invitation.userId === supervisor.id && 
      invitation.groupId === group.id
    );
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING':
        return 'warning';
      case 'TEAM_SUPERVISOR_APPROVED':
        return 'info';
      case 'PRIMARY_CAREGIVER_APPROVED':
        return 'success';
      case 'COACH_STAFF_APPROVED':
        return 'success';
      case 'APPROVED':
        return 'success';
      case 'REJECTED':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'PENDING':
        return 'Pending Review';
      case 'TEAM_SUPERVISOR_APPROVED':
        return 'Team Supervisor Approved';
      case 'PRIMARY_CAREGIVER_APPROVED':
        return 'Primary Caregiver Approved';
      case 'COACH_STAFF_APPROVED':
        return 'Coach/Staff Approved';
      case 'APPROVED':
        return 'Approved';
      case 'REJECTED':
        return 'Rejected';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom color="primary">
        Invite Team Supervisors to Your Teams
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {myGroups.length === 0 ? (
        <Card>
          <CardContent>
            <Typography variant="h6" color="text.secondary" align="center">
              You don't have any teams to invite supervisors to.
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
              Create a team first to start inviting supervisors.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {myGroups.map((group) => (
            <Grid item xs={12} md={6} lg={4} key={group.id}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <GroupIcon sx={{ mr: 1 }} />
                    <Typography variant="h6">{group.name}</Typography>
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {group.description || 'No description'}
                  </Typography>
                  
                  <Chip 
                    label={getGroupTypeName(group.groupTypeId)} 
                    size="small" 
                    sx={{ mb: 2 }}
                  />
                  
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Current Members:</strong>
                  </Typography>
                  
                  <Box sx={{ mb: 2 }}>
                    {group.userIds && group.userIds.length > 0 ? (
                      group.userIds.map((userId) => {
                        const user = allUsers.find(u => u.id === userId);
                        return (
                          <Chip 
                            key={userId} 
                            label={getSupervisorDisplayName(user)} 
                            size="small" 
                            variant="outlined"
                            sx={{ mr: 0.5, mb: 0.5 }}
                          />
                        );
                      })
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No members yet
                      </Typography>
                    )}
                  </Box>
                  
                  <Button
                    variant="contained"
                    startIcon={<SupervisorIcon />}
                    onClick={() => handleInviteSupervisor(group)}
                    fullWidth
                    disabled={!group.seekingMembers}
                  >
                    Invite Supervisor
                  </Button>
                  
                  {!group.seekingMembers && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Team is not currently seeking members
                    </Typography>
                  )}
                  
                  {/* Pending Invitations for this team */}
                  {(() => {
                    const teamPendingInvitations = pendingInvitations.filter(invitation => invitation.groupId === group.id);
                    return teamPendingInvitations.length > 0 ? (
                      <>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                          <EmailIcon sx={{ mr: 0.5, fontSize: '1rem' }} />
                          Pending Invitations
                        </Typography>
                        {teamPendingInvitations.map((invitation) => {
                          const supervisor = allUsers.find(u => u.id === invitation.userId);
                          return (
                            <Box key={invitation.id} sx={{ mb: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                              <Typography variant="body2" color="primary" sx={{ fontWeight: 'medium' }}>
                                {getSupervisorDisplayName(supervisor)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                <strong>Reason:</strong> {invitation.reason || 'No reason provided'}
                              </Typography>
                              <Chip 
                                label={getStatusLabel(invitation.status)} 
                                color={getStatusColor(invitation.status)}
                                size="small"
                                sx={{ mt: 0.5 }}
                              />
                            </Box>
                          );
                        })}
                      </>
                    ) : null;
                  })()}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Invitation Dialog */}
      <Dialog open={inviteDialogOpen} onClose={() => setInviteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <SupervisorIcon sx={{ mr: 1 }} />
            Invite Supervisor to {selectedGroup?.name}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Select Supervisor</InputLabel>
              <Select
                value={selectedSupervisor}
                onChange={(e) => setSelectedSupervisor(e.target.value)}
                label="Select Supervisor"
              >
                {supervisors
                  .filter(supervisor => selectedGroup ? !isSupervisorAlreadyInGroup(supervisor, selectedGroup) : true)
                  .map((supervisor) => (
                    <MenuItem key={supervisor.id} value={supervisor.id}>
                      {getSupervisorDisplayName(supervisor)}
                      {hasPendingInvitation(supervisor, selectedGroup) && (
                        <Chip 
                          label="Pending" 
                          size="small" 
                          color="warning" 
                          sx={{ ml: 1 }}
                        />
                      )}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Reason for Invitation"
              value={inviteReason}
              onChange={(e) => setInviteReason(e.target.value)}
              placeholder="Explain why you want to invite this supervisor to your team..."
              sx={{ mb: 2 }}
            />
            
            <Alert severity="info">
              The receiving supervisor will need to approve the invitation, followed by staff/coach approval.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteDialogOpen(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmitInvitation} 
            variant="contained" 
            disabled={submitting || !selectedSupervisor || !inviteReason.trim()}
          >
            {submitting ? <CircularProgress size={20} /> : 'Send Invitation'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TeamInviteSupervisors; 